import type { UmbMockDocumentTypeModel } from '../data/mock-data-set.types.js';
import { umbMockManager } from '../mock-manager.js';
import { UmbEntityMockDbBase } from './utils/entity/entity-base.js';
import { UmbMockEntityFolderManager } from './utils/entity/entity-folder.manager.js';
import { UmbMockEntityTreeManager } from './utils/entity/entity-tree.manager.js';
import { UmbMockEntityNamedItemManager } from './utils/entity/entity-named-item.manager.js';
import { UmbMockEntityDetailManager } from './utils/entity/entity-detail.manager.js';
import { pagedResult } from './utils/paged-result.js';
import { UmbId } from '@umbraco-cms/backoffice/id';
import {
	DataTypeChangeModeModel,
	type AllowedDocumentTypeModel,
	type CreateDocumentTypeRequestModel,
	type CreateFolderRequestModel,
	type DocumentTypeAllowedParentsResponseModel,
	type DocumentTypeConfigurationResponseModel,
	type DocumentTypeItemResponseModel,
	type DocumentTypeResponseModel,
	type DocumentTypeSortModel,
	type DocumentTypeTreeItemResponseModel,
	type PagedAllowedDocumentTypeModel,
	type ReferenceByIdModel,
} from '@umbraco-cms/backoffice/external/backend-api';

const defaultConfiguration: DocumentTypeConfigurationResponseModel = {
	dataTypesCanBeChanged: DataTypeChangeModeModel.TRUE,
	disableTemplates: false,
	useSegments: false,
	reservedFieldNames: [],
};

class UmbDocumentTypeMockDB extends UmbEntityMockDbBase<UmbMockDocumentTypeModel> {
	tree = new UmbMockEntityTreeManager<UmbMockDocumentTypeModel>(this, documentTypeTreeItemMapper);
	folder = new UmbMockEntityFolderManager<UmbMockDocumentTypeModel>(this, createMockDocumentTypeFolderMapper);
	item = new UmbMockEntityNamedItemManager<UmbMockDocumentTypeModel>(this, documentTypeItemMapper);
	detail = new UmbMockEntityDetailManager<UmbMockDocumentTypeModel>(
		this,
		createMockDocumentTypeMapper,
		documentTypeDetailMapper,
	);

	constructor(data: Array<UmbMockDocumentTypeModel>) {
		super('documentType', data);
	}

	getAllowedChildren(id: string, skip = 0, take = 100): PagedAllowedDocumentTypeModel {
		const documentType = this.detail.read(id);
		const allowedDocumentTypes = documentType.allowedDocumentTypes.map((sortModel: DocumentTypeSortModel) =>
			this.detail.read(sortModel.documentType.id),
		);
		const mappedItems = allowedDocumentTypes.map((item: UmbMockDocumentTypeModel) => allowedDocumentTypeMapper(item));
		return pagedResult(mappedItems, skip, take);
	}

	getAllowedAtRoot(skip = 0, take = 100): PagedAllowedDocumentTypeModel {
		const mockItems = this.data.filter((item) => item.allowedAsRoot);
		const mappedItems = mockItems.map((item) => allowedDocumentTypeMapper(item));
		return pagedResult(mappedItems, skip, take);
	}

	getAllowedInLibrary(): PagedAllowedDocumentTypeModel {
		const mockItems = this.data.filter((item) => item.allowedInLibrary);
		const mappedItems = mockItems.map((item) => allowedDocumentTypeMapper(item));
		return { items: mappedItems, total: mappedItems.length };
	}

	getAllowedParents(id: string): DocumentTypeAllowedParentsResponseModel {
		const allowedParentIds = this.data
			.filter((item) =>
				item.allowedDocumentTypes?.some((allowed: DocumentTypeSortModel) => allowed.documentType.id === id),
			)
			.map((item) => allowedParentDocumentTypeMapper(item));
		return { allowedParentIds };
	}

	getConfiguration(): DocumentTypeConfigurationResponseModel {
		return umbMockManager.getDataSet().documentTypeConfiguration ?? defaultConfiguration;
	}
}

const createMockDocumentTypeFolderMapper = (request: CreateFolderRequestModel): UmbMockDocumentTypeModel => {
	return {
		name: request.name,
		id: request.id ? request.id : UmbId.new(),
		parent: request.parent,
		description: '',
		alias: '',
		icon: '',
		properties: [],
		containers: [],
		allowedAsRoot: false,
		allowedInLibrary: false,
		variesByCulture: false,
		variesBySegment: false,
		isElement: false,
		allowedDocumentTypes: [],
		compositions: [],
		isFolder: true,
		hasChildren: false,
		allowedTemplates: [],
		cleanup: {
			preventCleanup: false,
			keepAllVersionsNewerThanDays: null,
			keepLatestVersionPerDayForDays: null,
		},
		flags: [],
		noAccess: false,
	};
};

const createMockDocumentTypeMapper = (request: CreateDocumentTypeRequestModel): UmbMockDocumentTypeModel => {
	return {
		name: request.name,
		id: request.id ? request.id : UmbId.new(),
		description: request.description,
		alias: request.alias,
		icon: request.icon,
		properties: request.properties,
		containers: request.containers,
		allowedAsRoot: request.allowedAsRoot,
		allowedInLibrary: request.allowedInLibrary,
		variesByCulture: request.variesByCulture,
		variesBySegment: request.variesBySegment,
		isElement: request.isElement,
		allowedDocumentTypes: request.allowedDocumentTypes,
		compositions: request.compositions,
		parent: request.parent,
		isFolder: false,
		hasChildren: false,
		allowedTemplates: [],
		cleanup: {
			preventCleanup: false,
			keepAllVersionsNewerThanDays: null,
			keepLatestVersionPerDayForDays: null,
		},
		flags: [],
		noAccess: false,
	};
};

const documentTypeDetailMapper = (item: UmbMockDocumentTypeModel): DocumentTypeResponseModel => {
	return {
		name: item.name,
		id: item.id,
		description: item.description,
		alias: item.alias,
		icon: item.icon,
		properties: item.properties,
		containers: item.containers,
		allowedAsRoot: item.allowedAsRoot,
		allowedInLibrary: item.allowedInLibrary,
		variesByCulture: item.variesByCulture,
		variesBySegment: item.variesBySegment,
		isElement: item.isElement,
		allowedDocumentTypes: item.allowedDocumentTypes,
		compositions: item.compositions,
		allowedTemplates: item.allowedTemplates,
		cleanup: item.cleanup,
		collection: item.collection,
	};
};

const documentTypeTreeItemMapper = (item: UmbMockDocumentTypeModel): DocumentTypeTreeItemResponseModel => {
	return {
		name: item.name,
		hasChildren: item.hasChildren,
		id: item.id,
		parent: item.parent,
		isFolder: item.isFolder,
		icon: item.icon,
		isElement: item.isElement,
		flags: item.flags,
		noAccess: item.noAccess,
	};
};

const documentTypeItemMapper = (item: UmbMockDocumentTypeModel): DocumentTypeItemResponseModel => {
	return {
		id: item.id,
		name: item.name,
		icon: item.icon,
		isElement: item.isElement,
		description: item.description ?? undefined,
		flags: item.flags,
		allowedInLibrary: item.allowedInLibrary,
	};
};

const allowedDocumentTypeMapper = (item: UmbMockDocumentTypeModel): AllowedDocumentTypeModel => {
	return {
		id: item.id,
		name: item.name,
		description: item.description,
		icon: item.icon,
	};
};

const allowedParentDocumentTypeMapper = (item: UmbMockDocumentTypeModel): ReferenceByIdModel => {
	return {
		id: item.id,
	};
};

export const umbDocumentTypeMockDb = new UmbDocumentTypeMockDB([]);
